import { useState } from "react";
import { csx } from "../../../utils";
import { copyTextToClipboard } from "../../../utils/copy";
import { Input } from "../BaseInput/Input";
import "./CopyInput.css";

export interface CopyInputProps {
  /** The name of the input field */
  name: string;

  /** The value to display in the input and copy to clipboard */
  value: string;

  /** Whether the input is disabled */
  isDisabled?: boolean;

  /** Optional callback when copy fails, receives the error object */
  onError?: (error: Error) => void;

  /** Optional callback when copy succeeds */
  onSuccess?: () => void;
}

/**
 * Input with a button to copy value to the clipboard.
 *
 * API:
 *  - {@link CopyInputProps}
 */
export function CopyInput({
  name,
  value,
  isDisabled,
  onError,
  onSuccess,
}: CopyInputProps) {
  const [isCopied, setCopied] = useState(false);

  return (
    <Input
      name={name}
      value={value}
      isDisabled={isDisabled}
      suffix={
        <CopyButton
          isCopied={isCopied}
          onClick={async () => {
            const copySuccess = await copyTextToClipboard(value, onError);
            if (!copySuccess) return;

            setCopied(true);
            onSuccess?.();
            setTimeout(() => setCopied(false), 5000);
          }}
        />
      }
    />
  );
}

const CopyButton = ({ isCopied, onClick }) => {
  return (
    <div className="proton-CopyInput-button-container" onClick={onClick}>
      <button
        data-testid="COPY_BUTTON"
        className={csx(
          "proton-CopyInput-button",
          isCopied && "proton-CopyInput-button--copied"
        )}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 512 512"
        >
          <path
            fill="currentColor"
            d="M408 480H184a72 72 0 0 1-72-72V184a72 72 0 0 1 72-72h224a72 72 0 0 1 72 72v224a72 72 0 0 1-72 72"
          />
          <path
            fill="currentColor"
            d="M160 80h235.88A72.12 72.12 0 0 0 328 32H104a72 72 0 0 0-72 72v224a72.12 72.12 0 0 0 48 67.88V160a80 80 0 0 1 80-80"
          />
        </svg>
        <span
          className={csx(
            "proton-CopyInput-button-text",
            isCopied && "proton-CopyInput-button-text--copied"
          )}
        >
          {isCopied ? "Copied!" : "Copy"}
        </span>
      </button>
    </div>
  );
};
